/**
 * Lightweight fetch wrapper that attaches the auth token and handles errors.
 *
 * Offline-aware: if the browser is offline (or the request fails with a
 * network error), mutating requests (POST/PUT/PATCH/DELETE) are queued in
 * IndexedDB via `mutationQueue` and a synthetic optimistic response is
 * returned. The SyncManager replays the queue on reconnect.
 *
 * Read requests (GET) never queue — they fail fast when offline so callers
 * can fall back to TanStack Query's persisted cache.
 */

import { mutationQueue, type MutationMethod } from '../features/offline/mutation-queue';
import { apiUrl } from './api-base';
import { offlineToast } from '../stores/offline-toast-store';

const TOKEN_KEY = 'tc-auth-token';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public payload?: unknown,
  ) {
    super(message);
  }
}

export class OfflineQueuedError extends Error {
  constructor(
    public queueId: string,
    message = 'Request queued for sync',
  ) {
    super(message);
  }
}

function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function isOnline(): boolean {
  return typeof navigator !== 'undefined' ? navigator.onLine : true;
}

const MUTATING: Set<string> = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers = new Headers(init.headers);
  headers.set('Content-Type', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const method = (init.method || 'GET').toUpperCase();
  const mutating = MUTATING.has(method);

  // Offline mutating path: queue and return synthetic optimistic response.
  if (mutating && !isOnline()) {
    return queueMutation<T>(path, method as MutationMethod, init);
  }

  let res: Response;
  try {
    res = await fetch(apiUrl(path), { ...init, headers });
  } catch (err) {
    // Network error on a mutation — queue it. Reads re-throw.
    if (mutating) {
      return queueMutation<T>(path, method as MutationMethod, init);
    }
    throw err;
  }

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let payload: unknown;
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
    throw new ApiError(res.status, `${res.status} ${res.statusText}`, payload);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

async function queueMutation<T>(
  path: string,
  method: MutationMethod,
  init: RequestInit,
): Promise<T> {
  const body = init.body ? JSON.parse(String(init.body)) : undefined;

  // Parse entityType/entityId from /entities/:type/:id patterns for conflict checks
  const entityMatch = path.match(/^\/entities\/([^/]+)(?:\/([^/?]+))?/);
  const entityType = entityMatch?.[1];
  const entityId = entityMatch?.[2];

  const label = deriveLabel(method, path, body);
  const queued = await mutationQueue.enqueue({
    method,
    path,
    body,
    entityType,
    entityId,
    intendedData: body as Record<string, unknown> | undefined,
    label,
  });

  // Notify the user that their action was queued
  offlineToast.queued(`Saved offline: ${label}`, 'Will sync automatically when back online');

  // Return a synthetic optimistic "result" so the caller's UI can update.
  // For create, we generate a temporary UUID-shaped ID so downstream routing
  // doesn't immediately break. Real server IDs replace it on replay.
  const synthetic: Record<string, unknown> = {
    __offlineQueued: true,
    __queueId: queued.id,
    id: entityId ?? `temp-${queued.id}`,
    ...(body && typeof body === 'object' ? (body as Record<string, unknown>) : {}),
    createdAt: new Date(queued.createdAt).toISOString(),
    updatedAt: new Date(queued.createdAt).toISOString(),
  };
  return synthetic as T;
}

function deriveLabel(method: MutationMethod, path: string, body: unknown): string {
  const verb = method === 'POST' ? 'Create' : method === 'DELETE' ? 'Delete' : 'Update';
  const segments = path.split('/').filter(Boolean);
  const kind = segments[1] ?? 'entity';
  const snippet =
    body && typeof body === 'object' && body !== null && 'description' in body
      ? ` · ${String((body as Record<string, unknown>).description ?? '').slice(0, 40)}`
      : '';
  return `${verb} ${kind.replace(/_/g, ' ')}${snippet}`;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  delete: <T = void>(path: string) => request<T>(path, { method: 'DELETE' }),
};

/**
 * Direct pass-through fetch that bypasses the offline queue — used by the
 * sync manager itself to replay queued mutations.
 */
export async function rawRequest<T>(path: string, init: RequestInit): Promise<T> {
  const token = getToken();
  const headers = new Headers(init.headers);
  headers.set('Content-Type', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(apiUrl(path), { ...init, headers });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let payload: unknown;
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
    throw new ApiError(res.status, `${res.status} ${res.statusText}`, payload);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}
